<template>
  <div>
    <!-- ── Section 1: App-level quota ─────────────────────────────── -->
    <section class="mb-5">
      <div class="d-flex align-items-start mb-3">
        <div>
          <h6 class="mb-0">ESC Application Quota</h6>
          <small class="text-muted">Global limits shared across all eshops</small>
        </div>
        <button class="ms-auto btn btn-sm btn-outline-secondary" :disabled="appLoading" @click="loadAppRules">
          <i class="bi-arrow-counterclockwise me-1"></i>{{ appLoading ? 'Loading…' : 'Refresh' }}
        </button>
      </div>

      <div v-if="appLoading" class="py-4 text-center text-muted small">Loading…</div>
      <div v-else-if="appError" class="alert alert-danger py-2">{{ appError }}</div>
      <EbayResourcesTable v-else-if="appData" :resources="appData.resources" v-model:search="appSearch" />
    </section>

    <!-- ── Section 2: Per-eshop quota ─────────────────────────────── -->
    <section>
      <h6 class="mb-3">Per-Seller Quota</h6>
      <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <select v-model="selectedEshopId" class="form-select" style="max-width:300px" :disabled="eshopsLoading">
          <option value="">{{ eshopsLoading ? 'Loading eshops…' : 'Select an eshop…' }}</option>
          <option v-for="e in eshops" :key="e.id" :value="e.id">{{ e.name }}</option>
        </select>
        <button class="btn btn-sm btn-primary" :disabled="!selectedEshopId || userLoading" @click="loadUserRules">
          {{ userLoading ? 'Checking…' : 'Check' }}
        </button>
      </div>

      <div v-if="userLoading" class="py-4 text-center text-muted small">Loading…</div>
      <div v-else-if="userError" class="alert alert-danger py-2">{{ userError }}</div>
      <template v-else-if="userData">
        <p class="text-muted small mb-3">
          Per-seller limits for <strong>{{ selectedEshopName }}</strong>
        </p>

        <!-- GetSellerEvents raw response (temporary testing ground) -->
        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <span class="small fw-semibold">GetSellerEvents</span>
            <span class="text-muted small">(last {{ eventsMinutes }} min)</span>
            <div class="input-group ms-2" style="max-width:120px">
              <input v-model.number="eventsMinutes" type="number" min="1" class="form-control form-control-sm" />
              <span class="input-group-text small">min</span>
            </div>
            <button class="btn btn-sm btn-outline-secondary" :disabled="eventsLoading" @click="reloadEvents">
              <i class="bi-arrow-counterclockwise"></i>
            </button>
          </div>
          <div v-if="eventsLoading" class="text-muted small py-1">Loading…</div>
          <div v-else-if="eventsError" class="alert alert-danger py-2 small">{{ eventsError }}</div>
          <pre v-else-if="eventsData" class="border rounded p-2 small bg-light" style="max-height:300px;overflow:auto;white-space:pre-wrap">{{ JSON.stringify(eventsData, null, 2) }}</pre>
        </div>

        <EbayResourcesTable :resources="userData.resources" v-model:search="userSearch" />
      </template>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import EbayResourcesTable from '@/admin/components/EbayResourcesTable.vue'

const store = useStore()

const appData    = ref(null)
const appLoading = ref(false)
const appError   = ref(null)
const appSearch  = ref('')

const eshops          = ref([])
const eshopsLoading   = ref(false)
const selectedEshopId = ref('')
const userData    = ref(null)
const userLoading = ref(false)
const userError   = ref(null)
const userSearch  = ref('')

const eventsData    = ref(null)
const eventsLoading = ref(false)
const eventsError   = ref(null)
const eventsMinutes = ref(1440)

const selectedEshopName = computed(() =>
  eshops.value.find(e => e.id === selectedEshopId.value)?.name || selectedEshopId.value
)

async function loadAppRules() {
  appLoading.value = true
  appError.value = null
  try {
    const res = await store.state.api.get('/admin/ebay/access-rules/app')
    appData.value = res.data
  } catch (e) {
    appError.value = e.response?.data?.error || e.message || 'Failed to load app access rules'
  } finally {
    appLoading.value = false
  }
}

async function loadEshops() {
  eshopsLoading.value = true
  try {
    const res = await store.state.api.get('/admin/eshops')
    eshops.value = res.data.eshops || []
  } catch (_) {
    // non-fatal
  } finally {
    eshopsLoading.value = false
  }
}

async function fetchSellerEvents() {
  if (!selectedEshopId.value) return
  eventsLoading.value = true
  eventsError.value = null
  eventsData.value = null
  try {
    const res = await store.state.api.get(`/admin/ebay/seller-events/${selectedEshopId.value}`, {
      params: { minutes: eventsMinutes.value }
    })
    eventsData.value = res.data
  } catch (e) {
    eventsError.value = e.response?.data?.error || e.message || 'Failed to load seller events'
  } finally {
    eventsLoading.value = false
  }
}

async function reloadEvents() {
  await fetchSellerEvents()
}

async function loadUserRules() {
  if (!selectedEshopId.value) return
  userLoading.value = true
  userError.value = null
  userData.value = null
  eventsData.value = null
  eventsError.value = null
  try {
    const [rulesRes] = await Promise.all([
      store.state.api.get(`/admin/ebay/access-rules/user/${selectedEshopId.value}`),
      fetchSellerEvents(),
    ])
    userData.value = rulesRes.data
  } catch (e) {
    userError.value = e.response?.data?.error || e.message || 'Failed to load user access rules'
  } finally {
    userLoading.value = false
  }
}

onMounted(() => {
  loadAppRules()
  loadEshops()
})
</script>
